import { type ComputedRef, type Ref, ref, watch } from 'vue';
import {
  addDays,
  addMonths,
  differenceInMilliseconds,
  format,
  fromUnixTime,
  getDaysInMonth,
  isAfter,
  isSameMonth,
  isToday,
  startOfDay,
  startOfMonth,
  subDays,
} from 'date-fns';

import type {
  ITimelineChartBar,
  ITimelineChartBaseRow,
  ITimelineChartMonthDates,
  ITimelineChartRow,
  ITimelineChartSubBar,
  ITimelineChartTimelineData,
} from '~/types';

import { getCategoricalTagColors } from './useColors';

const getRowShader = (bars: ITimelineChartBar[]) => {
  let leftMostBar = bars[0];
  let rightMostBar = bars[0];

  for (const bar of bars) {
    const left = bar.leftOffset;

    if (left < leftMostBar.leftOffset) {
      leftMostBar = bar;
    }

    if (left > rightMostBar.leftOffset) {
      rightMostBar = bar;
    }
  }

  return {
    leftOffset: leftMostBar.leftOffset,
    width:
      rightMostBar.leftOffset - leftMostBar.leftOffset + rightMostBar.width,
    color: leftMostBar.color,
  };
};

const getTimelineColumnCount = (months: ITimelineChartMonthDates[]): number => {
  return months.reduce((sum, month) => sum + month.monthDays.length, 0);
};

const getTimelineTodayColumnIndex = (
  months: ITimelineChartMonthDates[],
): number | null => {
  let todayColumnIndex: number | null = null;
  let currentIndex = 0;

  for (const month of months) {
    for (const day of month.monthDays) {
      if (day.isToday) {
        todayColumnIndex = currentIndex;
        break;
      }
      currentIndex++;
    }
  }

  return todayColumnIndex;
};

const getColumnWidth = (totalColumns: number, width: number) => {
  if (totalColumns === 0) return 0;

  const minWidth = 48;
  const columnWidth = width / totalColumns;

  if (columnWidth < minWidth) {
    return minWidth;
  }

  return columnWidth;
};

const getBarPosition = ({
  barStartTs,
  barEndTs,
  timelineStartTs,
  columnWidth,
}: {
  barStartTs: number;
  barEndTs: number;
  timelineStartTs: Date;
  columnWidth: number;
}): { leftOffset: number; width: number } => {
  const msPerDay = 24 * 60 * 60 * 1000;
  const pxPerMs = columnWidth / msPerDay;

  const offsetMs = differenceInMilliseconds(
    fromUnixTime(barStartTs),
    new Date(timelineStartTs),
  );

  const durationMs = differenceInMilliseconds(
    fromUnixTime(barEndTs),
    fromUnixTime(barStartTs),
  );

  const leftOffset = offsetMs * pxPerMs;
  const width = durationMs * pxPerMs;

  return { leftOffset, width };
};

const groupOverlappedBars = (subBars: ITimelineChartSubBar[]) => {
  const visitedBarsIds: (string | number)[] = [];
  const groupedBars: ITimelineChartSubBar[][] = [];

  for (const subBar of subBars) {
    if (visitedBarsIds.includes(subBar.id)) {
      continue;
    }

    const overlappedBars = subBars.filter((bar) => {
      if (bar.id === subBar.id) {
        return false;
      }

      const barEnd = bar.leftOffset + bar.width;
      const currentEnd = subBar.leftOffset + subBar.width;

      if (subBar.leftOffset < barEnd && currentEnd >= bar.leftOffset) {
        return true;
      }
    });

    if (!overlappedBars.length) {
      groupedBars.push([subBar]);
      visitedBarsIds.push(subBar.id);
      continue;
    }

    groupedBars.push([subBar, ...overlappedBars]);
    visitedBarsIds.push(subBar.id, ...overlappedBars.map(({ id }) => id));
  }

  return groupedBars;
};

const getOverlappedBarPosition = (bars: ITimelineChartSubBar[]) => {
  const groupedBars = groupOverlappedBars(bars);

  const subBars = groupedBars.flatMap((groupedBar) => {
    if (groupedBar.length === 1) {
      return groupedBar;
    }

    const maxBarWidth = Math.max(...groupedBar.map((bar) => bar.width));
    const currentBarWidth = maxBarWidth / groupedBar.length;
    const formattedGroupedBars: ITimelineChartSubBar[] = [];

    let leftOffset = Math.min(...groupedBar.map((bar) => bar.leftOffset));

    groupedBar.forEach((bar) => {
      formattedGroupedBars.push({
        ...bar,
        leftOffset: leftOffset,
        width: currentBarWidth,
      });

      leftOffset += currentBarWidth;
    });

    return formattedGroupedBars;
  });

  return subBars;
};

const getTimelineStartEndDates = ({ startTs, endTs, rows, bufferDays }) => {
  if (!startTs || !endTs) {
    const allBars = rows.flatMap((row) =>
      row.bars.flatMap((bar) => [bar.start, bar.end]),
    );

    startTs = Math.min(...allBars);
    endTs = Math.max(...allBars);
  }

  const startDate = subDays(startOfDay(fromUnixTime(startTs)), bufferDays);
  const endDate = addDays(fromUnixTime(endTs), bufferDays);

  return { startDate, endDate };
};

const getTimelineHeaderData = (
  startDate,
  endDate,
): ITimelineChartMonthDates[] => {
  const result: ITimelineChartMonthDates[] = [];
  let cursor = startOfMonth(startDate);

  while (isSameMonth(cursor, endDate) || isAfter(endDate, cursor)) {
    const currentYear = cursor.getFullYear();
    const currentMonth = cursor.getMonth();

    const isStartMonth =
      startDate.getFullYear() === currentYear &&
      startDate.getMonth() === currentMonth;

    const isEndMonth =
      endDate.getFullYear() === currentYear &&
      endDate.getMonth() === currentMonth;

    const monthDate = isStartMonth ? startDate.getDate() : 1;
    const dayEnd = isEndMonth ? endDate.getDate() : getDaysInMonth(cursor);

    const monthDays = Array.from({ length: dayEnd - monthDate + 1 }, (_, i) => {
      const date = new Date(currentYear, currentMonth, monthDate + i);
      return {
        day: date.getDate(),
        isToday: isToday(date),
      };
    });

    result.push({
      month: format(cursor, 'LLLL yyyy').toUpperCase(),
      monthDays,
    });

    cursor = addMonths(cursor, 1);
  }

  return result;
};

const getTimelineRows = ({
  rows,
  startDate,
  columnWidth,
  currentRows,
}: {
  currentRows: ITimelineChartRow[];
  rows: ITimelineChartBaseRow[];
  startDate: Date;
  columnWidth: number;
}): ITimelineChartRow[] => {
  let timelineRows: ITimelineChartRow[] = [];
  const selectedColors = {};
  let colorPointer = 0;

  const getBarColor = (id: number | string) => {
    if (!(id in selectedColors)) {
      const color = getCategoricalTagColors(colorPointer);
      selectedColors[id] = color;
      colorPointer += 1;
    }
    return selectedColors[id];
  };

  for (const row of [...rows]) {
    const currentRow = currentRows.find(
      (currentRow) => currentRow.id === row.id,
    );

    timelineRows.push({
      ...row,
      isOpen: currentRow?.isOpen ?? false,
      bars: row.bars.map((bar) => {
        const color = getBarColor(bar.groupId);

        return {
          id: bar.id,
          ...getBarPosition({
            barStartTs: bar.start,
            barEndTs: bar.end,
            timelineStartTs: startDate,
            columnWidth,
          }),
          details: bar.details,
          color,
          subBars: getOverlappedBarPosition(
            bar.subBars.map((subBar) => ({
              ...subBar,
              ...getBarPosition({
                barStartTs: subBar.start,
                barEndTs: subBar.end,
                timelineStartTs: startDate,
                columnWidth,
              }),
              details: subBar.details,
            })),
          ),
        };
      }),
    });
  }

  timelineRows = timelineRows.map((row) => ({
    ...row,
    shader: getRowShader(row.bars),
  }));

  return timelineRows;
};

export const useTimelineChartData = ({
  startTs,
  endTs,
  scrollableWidth,
  rows,
  bufferDays,
}: {
  startTs: ComputedRef<number | undefined>;
  endTs: ComputedRef<number | undefined>;
  rows: ComputedRef<ITimelineChartBaseRow[]>;
  bufferDays: ComputedRef<number | undefined>;
  scrollableWidth: Ref<number>;
}): ITimelineChartTimelineData => {
  const timelineRows = ref<ITimelineChartRow[]>();
  const headerData = ref<ITimelineChartMonthDates[]>([]);
  const totalColumns = ref(0);
  const todayColumnIndex = ref<number | null>(null);
  const columnWidth = ref(0);
  const scrollWidth = ref<string | 'auto'>('auto');
  const isOverflowing = ref(false);

  const toggleRowOpen = (id: number | string) => {
    const row = timelineRows.value.find((row) => row.id === id);
    if (row) row.isOpen = !row.isOpen;
  };

  watch(
    [startTs, endTs, bufferDays, rows, scrollableWidth],
    ([newStartTs, newEndTs, newBufferDays, newRows, newScrollableWidth]) => {
      const { startDate, endDate } = getTimelineStartEndDates({
        startTs: newStartTs,
        endTs: newEndTs,
        bufferDays: newBufferDays,
        rows: newRows,
      });

      headerData.value = getTimelineHeaderData(startDate, endDate);
      totalColumns.value = getTimelineColumnCount(headerData.value);
      todayColumnIndex.value = getTimelineTodayColumnIndex(headerData.value);
      columnWidth.value = getColumnWidth(
        totalColumns.value,
        newScrollableWidth,
      );
      const width = totalColumns.value * columnWidth.value;
      scrollWidth.value = `${width}px`;
      isOverflowing.value = width > newScrollableWidth;

      timelineRows.value = getTimelineRows({
        startDate,
        currentRows: timelineRows.value ?? [],
        rows: newRows,
        columnWidth: columnWidth.value,
      });
    },
    { immediate: true },
  );

  return {
    monthDates: headerData,
    totalColumns,
    timelineRows,
    columnWidth,
    todayColumnIndex,
    scrollWidth,
    isOverflowing,
    toggleRowOpen,
  };
};
